import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";

export default function ForcePasswordChange() {
  const { user, refreshMe, logout } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      await refreshMe();
      toast.success("Password updated — you're all set!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-brand-yellow p-4"
      data-testid="force-password-change"
    >
      <div className="brutal bg-white max-w-md w-full p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-brand-orange border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
            <KeyRound className="w-6 h-6 text-white" strokeWidth={2.4} />
          </div>
          <div>
            <h2 className="font-display font-black text-2xl leading-none">Welcome, {user?.name?.split(" ")[0]}!</h2>
            <p className="text-xs text-neutral-600 mt-1">Set a new password before you continue.</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold mb-1 block">Current password</label>
            <div className="relative">
              <input
                type={show.current ? "text" : "password"}
                className="input-brutal pr-12"
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                required
                autoFocus
                data-testid="fpc-current"
              />
              <button
                type="button"
                onClick={() => setShow((s) => ({ ...s, current: !s.current }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-black"
                aria-label="Toggle visibility"
              >
                {show.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold mb-1 block">New password</label>
            <div className="relative">
              <input
                type={show.next ? "text" : "password"}
                className="input-brutal pr-12"
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                required
                minLength={8}
                data-testid="fpc-new"
              />
              <button
                type="button"
                onClick={() => setShow((s) => ({ ...s, next: !s.next }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-black"
                aria-label="Toggle visibility"
              >
                {show.next ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">At least 8 characters.</div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold mb-1 block">Confirm new password</label>
            <input
              type={show.next ? "text" : "password"}
              className="input-brutal"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              required
              data-testid="fpc-confirm"
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            className="btn-brutal btn-primary w-full mt-4 py-3"
            data-testid="fpc-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {busy ? "Updating…" : "Update password"}
          </button>

          <button
            type="button"
            onClick={logout}
            className="text-xs text-neutral-500 underline w-full text-center mt-2"
            data-testid="fpc-logout"
          >
            Sign out
          </button>
        </form>
      </div>
    </div>
  );
}
